import {
  ActionIcon,
  Box,
  Group,
  Indicator,
  Loader,
  Menu,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconBell, IconCircleCheck } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/context/NotificationContext";
import { useUserContext } from "@/context/UserContext";
import NotificationInboxRow from "@/components/notifications/NotificationInboxRow";
import { NOTIFICATION_BORDER_COLOR, NOTIFICATION_NAVY } from "@/components/notifications/notificationDisplay.util";
import { getUpdatesPath } from "@/components/notifications/updatesPath.util";
import classes from "./NotificationBell.module.css";

const MENU_WIDTH = 380;
/** Mantine ActionIcon size="lg" — used to align panel right edge with bell left edge */
const BELL_TARGET_WIDTH = 42;

/** Approximate row height × 4 visible rows before scroll. */
const VISIBLE_NOTIFICATION_ROWS = 4;
const NOTIFICATION_ROW_APPROX_PX = 88;
const NOTIFICATION_LIST_SCROLL_HEIGHT = VISIBLE_NOTIFICATION_ROWS * NOTIFICATION_ROW_APPROX_PX;

type NotificationBellProps = {
  /** Icon-only trigger for collapsed sidebar rails */
  compact?: boolean;
  /** Styling for dark facilitator sidebar backgrounds */
  dark?: boolean;
};

export default function NotificationBell({ compact = false, dark = false }: NotificationBellProps) {
  const navigate = useNavigate();
  const { userDetails } = useUserContext();
  const { items, unreadCount, loading, refresh, markRead, markAllRead } = useNotifications();
  const updatesPath = getUpdatesPath(userDetails?.role);

  const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);
  const compactTooltip =
    unreadCount > 0 ? `Notifications (${unreadCount} unread)` : "Notifications";

  const handleOpen = () => {
    void refresh();
  };

  const handleItemClick = async (id: string, actionUrl: string | null, readAt: string | null) => {
    if (!readAt) {
      await markRead(id);
    }
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  const handleViewAll = () => {
    navigate(updatesPath);
  };

  return (
    <Menu
      position="bottom-end"
      width={MENU_WIDTH}
      shadow="md"
      onOpen={handleOpen}
      offset={{ mainAxis: 8, crossAxis: -(BELL_TARGET_WIDTH + 6) }}
    >
      <Menu.Target>
        {compact ? (
          <Tooltip label={compactTooltip} position="right" withArrow>
            <UnstyledButton
              aria-label="Notifications"
              w="100%"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 44,
                marginLeft: -8,
                marginRight: -8,
                paddingLeft: 8,
                paddingRight: 8,
                borderRadius: 10,
              }}
            >
              <Indicator
                inline
                label={badgeLabel}
                size={16}
                disabled={unreadCount === 0}
                color="red"
                offset={4}
              >
                <IconBell size={22} stroke={1.8} color="rgba(255, 255, 255, 0.95)" />
              </Indicator>
            </UnstyledButton>
          </Tooltip>
        ) : (
          <Indicator
            inline
            label={badgeLabel}
            size={16}
            disabled={unreadCount === 0}
            color="red"
            offset={4}
          >
            <ActionIcon
              size="lg"
              variant={dark ? "subtle" : "light"}
              color={dark ? "gray.0" : undefined}
              radius="xl"
              aria-label="Notifications"
              style={
                dark
                  ? {
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                      color: "rgba(255, 255, 255, 0.95)",
                    }
                  : undefined
              }
            >
              <IconBell size={18} />
            </ActionIcon>
          </Indicator>
        )}
      </Menu.Target>
      <Menu.Dropdown
        p={0}
        style={{
          overflow: "hidden",
          border: `1px solid ${NOTIFICATION_NAVY}`,
        }}
      >
        <Group
          justify="space-between"
          align="center"
          wrap="nowrap"
          px="md"
          py="sm"
          style={{ borderBottom: `1px solid ${NOTIFICATION_BORDER_COLOR}` }}
        >
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon
              size={36}
              radius="xl"
              variant="light"
              color="blue"
              style={{ backgroundColor: "var(--mantine-color-blue-0)" }}
            >
              <IconBell size={18} stroke={1.75} color="var(--mantine-color-blue-6)" />
            </ThemeIcon>
            <Text fw={700} size="sm" c="dark.7">
              Notifications
            </Text>
          </Group>
          {unreadCount > 0 && (
            <UnstyledButton
              onClick={() => void markAllRead()}
              style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}
            >
              <IconCircleCheck size={16} color="var(--mantine-color-blue-6)" stroke={1.75} />
              <Text size="sm" fw={500} c="blue.6">
                Mark all read
              </Text>
            </UnstyledButton>
          )}
        </Group>

        <Box
          className={classes.listScroll}
          style={{ maxHeight: NOTIFICATION_LIST_SCROLL_HEIGHT }}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {loading && items.length === 0 ? (
            <Box py="xl" style={{ display: "flex", justifyContent: "center" }}>
              <Loader size="sm" />
            </Box>
          ) : items.length === 0 ? (
            <Text size="sm" c="dimmed" px="md" py="lg" ta="center">
              You don&apos;t have any notifications
            </Text>
          ) : (
            items.map((n) => (
              <NotificationInboxRow
                key={n.id}
                item={n}
                onClick={() => void handleItemClick(n.id, n.actionUrl, n.readAt)}
              />
            ))
          )}
        </Box>
        <Box
          px="md"
          py="sm"
          style={{ borderTop: `1px solid ${NOTIFICATION_BORDER_COLOR}` }}
        >
          <UnstyledButton w="100%" onClick={handleViewAll}>
            <Text size="sm" fw={600} c="blue.6" ta="center">
              View all updates
            </Text>
          </UnstyledButton>
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
}
